<template>
  <article>
    <LoaderStack v-if="isLoading" />
    <template v-else>
      <component
        :is="provideComponentName(item.code)"
        v-for="item in sections"
        :key="hash(item)"
        :section-data="item"
      />
    </template>
  </article>
</template>

<script setup lang="ts">
  import hash from 'object-hash';
  import type { ConcreteComponent } from 'vue';

  const { t } = useI18n({ useScope: 'local' });

  const Banner = resolveComponent('AreaPartnersBanner');
  const Footer = resolveComponent('AreaPartnersFooter');
  const KeyValues = resolveComponent('AreaPartnersKeyValues');
  const ProgramTypes = resolveComponent('AreaPartnersProgramTypes');
  const Resources = resolveComponent('AreaPartnersResources');
  const Steps = resolveComponent('AreaPartnersSteps');
  const Stories = resolveComponent('AreaPartnersStories');
  const Trusted = resolveComponent('AreaPartnersTrusted');

  const partnersStore = usePartnersStore();
  const { sections } = storeToRefs(partnersStore);
  const { provideWebTitle } = useWebsite();

  const { status: GET_PARTNERS_PAGE_STATUS } =
    await partnersStore.USE_GET_PARTNERS_PAGE();
  const { status: GET_TRUSTED_PARTNERS_STATUS } =
    await partnersStore.USE_GET_TRUSTED_PARTNERS();

  const isLoading = computed(
    (): boolean =>
      GET_PARTNERS_PAGE_STATUS.value === 'pending' ||
      GET_TRUSTED_PARTNERS_STATUS.value === 'pending'
  );

  const provideComponentName = (
    code: string | null
  ): string | ConcreteComponent => {
    switch (code) {
      case 'partners-banner':
        return Banner;
      case 'partners-footer':
        return Footer;
      case 'partners-key-values':
        return KeyValues;
      case 'partners-program-types':
        return ProgramTypes;
      case 'partners-resources':
        return Resources;
      case 'partners-steps':
        return Steps;
      case 'partners-stories':
        return Stories;
      case 'partners-trusted':
        return Trusted;
      default:
        return '';
    }
  };

  useSeoMeta({
    title: () => t('seo-title'),
    ogTitle: () => provideWebTitle(t('seo-title')),
    twitterTitle: () => provideWebTitle(t('seo-title')),
    description: () => t('seo-description'),
    ogDescription: () => t('seo-description'),
    twitterDescription: () => t('seo-description'),
  });
</script>

<i18n lang="json">
{
  "en-int": {
    "seo-title": "Partner Network",
    "seo-description": "Unlock new opportunities and achieve shared success by joining our trusted partner ecosystem."
  },
  "en-jp": {
    "seo-title": "Partner Network",
    "seo-description": "Unlock new opportunities and achieve shared success by joining our trusted partner ecosystem."
  },
  "en-tw": {
    "seo-title": "Partner Network",
    "seo-description": "Unlock new opportunities and achieve shared success by joining our trusted partner ecosystem."
  },
  "en-kr": {
    "seo-title": "Partner Network",
    "seo-description": "Unlock new opportunities and achieve shared success by joining our trusted partner ecosystem."
  },
  "en-americas": {
    "seo-title": "Partner Network",
    "seo-description": "Unlock new opportunities and achieve shared success by joining our trusted partner ecosystem."
  },
  "en-asia-pacific": {
    "seo-title": "Partner Network",
    "seo-description": "Unlock new opportunities and achieve shared success by joining our trusted partner ecosystem."
  },
  "en-emea": {
    "seo-title": "Partner Network",
    "seo-description": "Unlock new opportunities and achieve shared success by joining our trusted partner ecosystem."
  },
  "ja-jp": {
    "seo-title": "パートナー",
    "seo-description": "進化し続ける市場で、成長と革新を共に追求できるパートナーエコシステムに参加しましょう。"
  },
  "zh-cn": {
    "seo-title": "合作伙伴",
    "seo-description": "与我们合作，推动创新，共创双赢，在市场取得成功。"
  },
  "zh-tw": {
    "seo-title": "合作夥伴",
    "seo-description": "加入我們的合作夥伴生態系，攜手實現成長與創新，共創成功。"
  }
}
</i18n>
